import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDepartments } from '../../../hooks/useDepartments';
import Modal from '../../ui/Modal';
import ProjectStatusBadge from './ProjectStatusBadge';
import {
  formatFinancialRequirement,
  formatPriority,
  PROJECT_STATUS
} from '../../../constants/projectConstants';
import { getSectorLabel } from '../../../constants/authConstants';
import { dateUtils } from '../../../utils/helpers';

const ProjectDetailsModal = ({ isOpen, onClose, project }) => {
  const { isMobile, getResponsiveValue } = useTheme();
  const { getDepartmentLabel } = useDepartments();

  const containerPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  const gridCols = getResponsiveValue({
    mobile: 'grid-cols-1',
    desktop: 'grid-cols-2'
  });

  const gridGap = getResponsiveValue({
    mobile: 'gap-6',
    desktop: 'gap-8'
  });

  if (!project) return null;

  const isApproved = project.status === PROJECT_STATUS.APPROVED;
  const hasPriority = project.priority !== null && project.priority !== undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Project Details"
      size="lg"
      maxHeight="large"
    >
      <div className={containerPadding}>
        <div className="space-y-6">
          <div className="pb-4 border-b border-border-primary">
            <h3 className="text-xl font-semibold text-text-primary mb-3">
              {project.projectName}
            </h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <ProjectStatusBadge status={project.status} />
                {hasPriority && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-text-secondary">Priority:</span>
                    <div className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-full font-bold text-sm">
                      {formatPriority(project.priority)}
                    </div>
                  </div>
                )}
                <span className="text-sm text-text-tertiary">
                  Submitted {dateUtils.formatDate(project.createdAt)}
                </span>
              </div>
            </div>
          </div>

          <div className={`grid ${gridCols} ${gridGap}`}>
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-text-secondary mb-2">SECTOR</h4>
                <p className="text-text-primary">{getSectorLabel(project.sector)}</p>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-text-secondary mb-2">DEPARTMENT</h4>
                <p className="text-text-primary">{getDepartmentLabel(project.department)}</p>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-text-secondary mb-2">FINANCIAL YEAR</h4>
                <p className="text-text-primary">
                  {project.financialYear ? `FY ${project.financialYear}` : 'Not specified'}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-text-secondary mb-2">PROPOSED TOTAL COST (in lakh)</h4>
                <p className="text-text-primary font-semibold text-lg">
                  {formatFinancialRequirement(project.financialRequirement)}
                </p>
              </div>

              {isApproved && (
                <div>
                  <h4 className="text-sm font-semibold text-text-secondary mb-2">STATUS</h4>
                  <p className="text-success font-semibold">
                    Approved on {dateUtils.formatDate(project.approvedAt)}
                  </p>
                  {project.approvedAmount && (
                    <div className="mt-2">
                      <h4 className="text-sm font-semibold text-text-secondary mb-1">APPROVED TOTAL COST (in lakh)</h4>
                      <p className="text-success font-semibold text-lg">
                        {formatFinancialRequirement(project.approvedAmount)}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-text-secondary mb-3">PROJECT DESCRIPTION</h4>
            <div className="bg-surface-secondary rounded-lg p-4">
              <p className="text-text-primary leading-relaxed whitespace-pre-line">
                {project.brief}
              </p>
            </div>
          </div>

          {(project.clearanceType || project.allocatedAmount || project.uoNumber || project.clearanceDate) && (
            <div>
              <h4 className="text-sm font-semibold text-text-secondary mb-3">CLEARANCE INFORMATION</h4>
              <div className="bg-surface-secondary rounded-lg p-4">
                <div className={`grid ${gridCols} gap-4`}>
                  {project.clearanceType && (
                    <div>
                      <h5 className="text-xs font-medium text-text-tertiary mb-1">CLEARANCE TYPE</h5>
                      <p className="text-text-primary capitalize">
                        {project.clearanceType.replace('_', ' ')}
                      </p>
                    </div>
                  )}
                  
                  {project.allocatedAmount && (
                    <div>
                      <h5 className="text-xs font-medium text-text-tertiary mb-1">ALLOCATED AMOUNT (in lakh)</h5>
                      <p className="text-text-primary font-medium">
                        {formatFinancialRequirement(project.allocatedAmount)}
                      </p>
                    </div>
                  )}
                  
                  {project.uoNumber && (
                    <div>
                      <h5 className="text-xs font-medium text-text-tertiary mb-1">UO NUMBER</h5>
                      <p className="text-text-primary">{project.uoNumber}</p>
                    </div>
                  )}
                  
                  {project.clearanceDate && (
                    <div>
                      <h5 className="text-xs font-medium text-text-tertiary mb-1">CLEARANCE DATE</h5>
                      <p className="text-text-primary">{dateUtils.formatDate(project.clearanceDate)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ProjectDetailsModal;